import type { UseMutationResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { OSAT_API_ROUTES, OsatQueryKeys } from '../constants/osatRoutes';
import { usePatchData } from '../services/apiClient';

/**
 * Response of `PATCH /v1.0/osat/dismiss`.
 * The client does not consume the body — same-day suppression is server-side.
 */
export interface OsatDismissResponse {
  lastDismissedDate?: string;
}

/**
 * Dismisses the OSAT survey for the current user.
 *
 * `PATCH /v1.0/osat/dismiss` (no body). Called from both dismiss affordances
 * ("X" and "Responder depois") before the modal closes. The server records
 * `lastDismissedDate` so the survey is not re-shown the same day and reappears
 * on the next-day login — no client-side guard needed.
 */
export const useDismissOsat = (): UseMutationResult<OsatDismissResponse, AxiosError, void> => {
  const baseUrl = import.meta.env.VITE_FOUNDATION_API_BASE_URL as string;
  const queryClient = useQueryClient();

  return usePatchData<OsatDismissResponse, void>(OSAT_API_ROUTES.DISMISS(baseUrl), {
    // On success the eligibility query is invalidated so the OSAT trigger
    // re-consults the server instead of replaying a stale `eligible: true`
    // still cached within its 5-min freshness window (#806378). Mirrors
    // `useAcknowledgeAlertBar`'s post-mutation invalidation.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [OsatQueryKeys.ELIGIBILITY] });
    },
  });
};
